import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, RadarChart,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { fmtCurrency } from '../lib/localeUtils';
import { HeadOfficeCompare } from '../components/HeadOfficeCompare';
import {
  Building2, DollarSign, Package, AlertTriangle, TrendingUp, ShoppingCart,
  Loader2, BarChart3
} from 'lucide-react';

// ── Material-feel tokens (shared with DashboardPage) ──
const TILE_BASE = "relative overflow-hidden bg-white border border-slate-100 rounded-2xl p-5 transition-all duration-300 ease-out shadow-[0_2px_12px_-4px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_32px_-8px_rgba(0,0,0,0.14)] hover:border-slate-200 hover:-translate-y-1";
const CHART_CARD = "bg-white border border-slate-100/80 rounded-[1.25rem] shadow-[0_4px_24px_-8px_rgba(0,0,0,0.08)] animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out p-6";

const ACCENTS = {
  emerald: { grad: "from-emerald-50 to-white", iconBg: "bg-emerald-100", iconFg: "text-emerald-600", text: "text-emerald-700" },
  sky:     { grad: "from-sky-50 to-white",     iconBg: "bg-sky-100",     iconFg: "text-sky-600",     text: "text-sky-700" },
  amber:   { grad: "from-amber-50 to-white",   iconBg: "bg-amber-100",   iconFg: "text-amber-600",   text: "text-amber-700" },
  indigo:  { grad: "from-indigo-50 to-white",  iconBg: "bg-indigo-100",  iconFg: "text-indigo-600",  text: "text-indigo-700" },
};

const CHART_COLORS = [
  'hsl(158 64% 42%)',   // emerald base, slightly darker than accent
  'hsl(217 91% 55%)',   // ocean blue
  'hsl(38 92% 50%)',    // amber
  'hsl(243 75% 60%)',   // indigo
  'hsl(346 87% 55%)',   // rose
];

const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: '7d',    label: 'Last 7 days' },
  { value: '30d',   label: 'Last 30 days' },
];

export default function BranchPerformancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await api.get('/analytics/branch-performance');
        setData(res.data);
      } catch (err) {
        console.error('Branch performance error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="branch-performance-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data || data.branches.length === 0) {
    const allInactive = data && data.total_branch_count > 0 && data.active_branch_count === 0;
    return (
      <div className="space-y-4" data-testid="branch-performance-page">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Branch Performance
        </h1>
        <div className={`${CHART_CARD} py-12 text-center text-slate-500`}>
          {allInactive
            ? 'All branches are currently inactive. Activate at least one branch to see performance data.'
            : 'No branch data available'}
        </div>
      </div>
    );
  }

  const { branches, totals, total_branch_count, active_branch_count } = data;
  const revenueKey = period === '7d' ? 'revenue_7d' : period === 'today' ? 'revenue_today' : 'revenue_30d';
  const salesKey   = period === '7d' ? 'sales_7d'   : period === 'today' ? 'sales_today'   : 'sales_30d';

  const revenueChartData = branches.map(b => ({
    name: b.name.length > 18 ? b.name.slice(0, 18) + '...' : b.name,
    revenue: b[revenueKey],
    sales: b[salesKey],
  }));

  const stockChartData = branches.map(b => ({
    name: b.name.length > 18 ? b.name.slice(0, 18) + '...' : b.name,
    healthy: Math.max(0, b.total_products - b.low_stock - b.out_of_stock),
    low_stock: b.low_stock,
    out_of_stock: b.out_of_stock,
  }));

  const maxRevenue = Math.max(...branches.map(b => b.revenue_30d), 1);
  const maxProducts = Math.max(...branches.map(b => b.total_products), 1);
  const maxSales = Math.max(...branches.map(b => b.sales_30d), 1);
  const radarMetrics = ['Revenue', 'Products', 'Sales Vol', 'Suppliers', 'Stock Health'];
  const radarData = radarMetrics.map((metric, i) => {
    const row = { metric };
    branches.forEach((b) => {
      let val = 0;
      if (i === 0) val = (b.revenue_30d / maxRevenue) * 100;
      else if (i === 1) val = (b.total_products / maxProducts) * 100;
      else if (i === 2) val = (b.sales_30d / maxSales) * 100;
      else if (i === 3) val = Math.min(b.total_suppliers * 20, 100);
      else val = b.total_products > 0 ? ((b.total_products - b.low_stock - b.out_of_stock) / b.total_products) * 100 : 0;
      row[b.name] = Math.round(val);
    });
    return row;
  });

  const allDays = new Set();
  branches.forEach(b => b.daily_trend.forEach(d => allDays.add(d.date)));
  const combinedTrend = Array.from(allDays).sort().map(date => {
    const row = { date: date.slice(5) };
    branches.forEach(b => {
      const dayData = b.daily_trend.find(d => d.date === date);
      row[b.name] = dayData ? dayData.revenue : 0;
    });
    return row;
  });

  const summaryStats = [
    { label: 'Total Revenue',   value: fmtCurrency(totals[revenueKey]),                                     icon: DollarSign,   accent: 'emerald' },
    { label: 'Total Sales',     value: branches.reduce((s, b) => s + b[salesKey], 0),                       icon: ShoppingCart, accent: 'sky' },
    { label: 'Total Products',  value: totals.total_products,                                               icon: Package,      accent: 'indigo' },
    { label: 'Low Stock Items', value: totals.low_stock,                                                    icon: AlertTriangle, accent: 'amber', danger: totals.low_stock > 0 },
  ];

  return (
    <div className="space-y-6" data-testid="branch-performance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <span className="h-10 w-10 rounded-2xl bg-gradient-to-br from-emerald-100 to-sky-100 text-emerald-700 flex items-center justify-center shadow-sm">
              <BarChart3 className="h-5 w-5" />
            </span>
            Head office view
          </h1>
          <p className="text-sm text-slate-500 mt-1 ml-[52px]">
            Sales, stock and staff for {active_branch_count} of {total_branch_count} branch{total_branch_count !== 1 ? 'es' : ''} (active only)
          </p>
        </div>

        {/* Segmented period filter */}
        <div className="inline-flex items-center gap-1 p-1 rounded-full bg-slate-100" data-testid="period-selector">
          {PERIODS.map(p => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPeriod(p.value)}
              aria-pressed={period === p.value}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all duration-200 ${
                period === p.value
                  ? 'bg-white text-[#0A4D3C] shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryStats.map(s => {
          const a = ACCENTS[s.accent] || ACCENTS.emerald;
          return (
            <div key={s.label} className={TILE_BASE} data-testid={`summary-${s.label.toLowerCase().replace(/ /g, '-')}`}>
              <div className={`absolute inset-0 bg-gradient-to-br ${a.grad} opacity-70 z-0 pointer-events-none`} />
              <div className="relative z-10 flex items-start justify-between mb-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${a.iconBg} ${a.iconFg} shadow-sm`}>
                  <s.icon className="h-5 w-5" />
                </div>
                {s.danger && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200">
                    Alert
                  </span>
                )}
              </div>
              <div className="relative z-10">
                <p className={`text-2xl md:text-3xl font-bold tracking-tight ${s.danger ? 'text-rose-600' : a.text}`} style={{ fontFamily: 'Outfit' }}>
                  {s.value}
                </p>
                <p className="text-xs font-semibold text-slate-700 mt-2">{s.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      <HeadOfficeCompare branches={branches} period={period} />

      {/* Revenue + Stock health side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={CHART_CARD} data-testid="revenue-chart">
          <ChartHeader icon={DollarSign} title="Revenue by Branch" accent="emerald" />
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={revenueChartData} margin={{ left: 10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} />
              <Tooltip
                cursor={{ fill: 'rgba(16, 185, 129, 0.05)' }}
                contentStyle={chartTooltipStyle}
                formatter={(val) => fmtCurrency(val)}
              />
              <defs>
                <linearGradient id="revBarGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.55} />
                </linearGradient>
              </defs>
              <Bar dataKey="revenue" fill="url(#revBarGrad)" radius={[10, 10, 0, 0]} name="Revenue" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className={CHART_CARD} data-testid="stock-chart">
          <ChartHeader icon={Package} title="Stock Health by Branch" accent="indigo" />
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stockChartData} margin={{ left: 10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={chartTooltipStyle} cursor={{ fill: 'rgba(99, 102, 241, 0.05)' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="healthy"     stackId="a" fill="hsl(158 64% 42%)" name="Healthy" />
              <Bar dataKey="low_stock"   stackId="a" fill="hsl(38 92% 55%)"  name="Low Stock" />
              <Bar dataKey="out_of_stock" stackId="a" fill="hsl(346 87% 55%)" name="Out of Stock" radius={[10, 10, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Daily trend */}
      {combinedTrend.length > 0 && (
        <div className={CHART_CARD} data-testid="daily-trend-chart">
          <ChartHeader icon={TrendingUp} title="Daily Revenue Trend (7 Days)" accent="sky" />
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={combinedTrend} margin={{ left: 10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={chartTooltipStyle} formatter={(val) => fmtCurrency(val)} cursor={{ fill: 'rgba(14, 165, 233, 0.05)' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {branches.map((b, i) => (
                <Bar key={b.tenant_id} dataKey={b.name} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[6, 6, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Radar */}
      {branches.length >= 2 && (
        <div className={CHART_CARD} data-testid="radar-chart">
          <ChartHeader icon={Building2} title="Branch Comparison Radar" accent="amber" subtitle="Normalized scores (0-100) across key metrics" />
          <ResponsiveContainer width="100%" height={320}>
            <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="75%">
              <PolarGrid stroke="#E5E7EB" />
              <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11, fill: '#6B7280' }} />
              <PolarRadiusAxis tick={{ fontSize: 9, fill: '#94A3B8' }} domain={[0, 100]} />
              {branches.map((b, i) => (
                <Radar
                  key={b.tenant_id}
                  name={b.name}
                  dataKey={b.name}
                  stroke={CHART_COLORS[i % CHART_COLORS.length]}
                  fill={CHART_COLORS[i % CHART_COLORS.length]}
                  fillOpacity={0.18}
                  strokeWidth={2}
                />
              ))}
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Tooltip contentStyle={chartTooltipStyle} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Details table */}
      <div className={CHART_CARD} data-testid="branch-details-table">
        <ChartHeader icon={BarChart3} title="Detailed Branch KPIs" accent="emerald" />
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-slate-100 hover:bg-transparent">
                <TableHead className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Branch</TableHead>
                <TableHead className="text-right text-xs text-slate-500 font-semibold uppercase tracking-wide">Revenue</TableHead>
                <TableHead className="text-right text-xs text-slate-500 font-semibold uppercase tracking-wide">Sales</TableHead>
                <TableHead className="text-right text-xs text-slate-500 font-semibold uppercase tracking-wide">Products</TableHead>
                <TableHead className="text-right text-xs text-slate-500 font-semibold uppercase tracking-wide">Low Stock</TableHead>
                <TableHead className="text-right text-xs text-slate-500 font-semibold uppercase tracking-wide">Out of Stock</TableHead>
                <TableHead className="text-right text-xs text-slate-500 font-semibold uppercase tracking-wide">Suppliers</TableHead>
                <TableHead className="text-right text-xs text-slate-500 font-semibold uppercase tracking-wide">Pending POs</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {branches.map((b, i) => (
                <TableRow key={b.tenant_id} data-testid={`branch-row-${b.tenant_id}`} className="hover:bg-slate-50/70 border-b border-slate-50">
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                      <span className="font-medium text-slate-800">{b.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-semibold text-emerald-700 tabular-nums">{fmtCurrency(b[revenueKey])}</TableCell>
                  <TableCell className="text-right tabular-nums">{b[salesKey]}</TableCell>
                  <TableCell className="text-right tabular-nums">{b.total_products}</TableCell>
                  <TableCell className="text-right">
                    {b.low_stock > 0 ? (
                      <Badge className="rounded-full bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100">{b.low_stock}</Badge>
                    ) : <span className="text-slate-400">0</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    {b.out_of_stock > 0 ? (
                      <Badge className="rounded-full bg-rose-100 text-rose-700 border-rose-200 hover:bg-rose-100">{b.out_of_stock}</Badge>
                    ) : <span className="text-slate-400">0</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{b.total_suppliers}</TableCell>
                  <TableCell className="text-right tabular-nums">{b.pending_pos}</TableCell>
                </TableRow>
              ))}
              <TableRow className="border-t-2 border-slate-200 font-bold hover:bg-transparent bg-slate-50/60">
                <TableCell className="text-slate-900">Total</TableCell>
                <TableCell className="text-right text-emerald-800 tabular-nums">{fmtCurrency(branches.reduce((s, b) => s + b[revenueKey], 0))}</TableCell>
                <TableCell className="text-right tabular-nums">{branches.reduce((s, b) => s + b[salesKey], 0)}</TableCell>
                <TableCell className="text-right tabular-nums">{totals.total_products}</TableCell>
                <TableCell className="text-right tabular-nums">{totals.low_stock}</TableCell>
                <TableCell className="text-right tabular-nums">{totals.out_of_stock}</TableCell>
                <TableCell className="text-right tabular-nums">{branches.reduce((s, b) => s + b.total_suppliers, 0)}</TableCell>
                <TableCell className="text-right tabular-nums">{branches.reduce((s, b) => s + b.pending_pos, 0)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

const chartTooltipStyle = {
  background: '#fff',
  border: '1px solid #E5E7EB',
  borderRadius: 12,
  fontSize: 12,
  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
  padding: '8px 12px',
};

const ChartHeader = ({ icon: Icon, title, subtitle, accent = 'emerald' }) => {
  const a = ACCENTS[accent] || ACCENTS.emerald;
  return (
    <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-4">
      <div className="flex items-center gap-3">
        <span className={`h-8 w-8 rounded-lg ${a.iconBg} ${a.iconFg} flex items-center justify-center`}>
          <Icon className="h-4 w-4" />
        </span>
        <div>
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
};
